// Building Store - 完全版

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { 
  BuildingResult, 
  SavedBuilding, 
  BuildingExportData,
  BuildingConnection,
  BuildingInput
} from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

// ====================================
// インターフェース定義
// ====================================

interface BuildingStore {
  // データ
  buildings: SavedBuilding[]
  maxBuildings: number
  
  // 単体操作
  addBuilding: (building: Omit<SavedBuilding, 'id' | 'createdAt' | 'updatedAt'>) => void
  addBatchBuildings: (buildings: Omit<SavedBuilding, 'id' | 'createdAt' | 'updatedAt'>[]) => void
  saveBuilding: (building: BuildingResult, input?: BuildingInput) => Promise<void>
  updateBuilding: (id: string, updates: Partial<SavedBuilding>) => void
  deleteBuilding: (id: string) => void
  removeBuilding: (id: string) => void // エイリアス
  
  // 取得・検索
  getBuildingById: (id: string) => SavedBuilding | undefined
  getBuildingsByWorld: (worldId: string) => SavedBuilding[]
  getBuildingsByCharacter: (characterId: string) => SavedBuilding[]
  getBuildingsByItem: (itemId: string) => SavedBuilding[]
  searchBuildings: (query: string) => SavedBuilding[]
  
  // お気に入り・タグ
  toggleFavorite: (id: string) => void
  addTag: (id: string, tag: string) => void
  removeTag: (id: string, tag: string) => void
  updateMemo: (id: string, memo: string) => void
  
  // 建造物間の関係
  linkBuildings: (id1: string, id2: string, relationship: string) => void
  unlinkBuildings: (id1: string, id2: string) => void
  getLinkedBuildings: (id: string) => SavedBuilding[]
  
  // 地区管理
  createDistrict: (buildingIds: string[], districtName: string) => void
  getDistrictBuildings: (districtName: string) => SavedBuilding[]
  
  // データ整合性
  fixDuplicateIds: () => boolean
  validateBuildingData: (building: SavedBuilding) => boolean
  cleanupOrphanedRelations: () => void
  
  // インポート/エクスポート
  importBuildings: (buildings: SavedBuilding[] | BuildingExportData) => void
  exportBuildings: (buildingIds?: string[]) => void
  exportAsJSON: () => string
  exportAsText: () => void
  
  // 一括操作
  clearAllBuildings: () => void
  clearAll: () => void // エイリアス
  deleteMultiple: (ids: string[]) => void
  batchUpdateWorld: (buildingIds: string[], worldId: string) => void
  
  // 統計情報
  getStatistics: () => {
    total: number
    byScale: Record<string, number>
    byType: Record<string, number>
    byWorld: Record<string, number>
    favorites: number
  }
}

// ====================================
// ストア実装
// ====================================

const MAX_BUILDINGS = 100

export const useBuildingStore = create<BuildingStore>()(
  persist(
    (set, get) => ({
      buildings: [],
      maxBuildings: MAX_BUILDINGS,

      // ====================================
      // 単体操作
      // ====================================

      addBuilding: (building) => {
        const newBuilding: SavedBuilding = {
          ...building,
          id: generateBuildingId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
        
        set(state => ({
          buildings: enforceMaxLimit(
            [newBuilding, ...state.buildings],
            state.maxBuildings
          )
        }))
      },

      addBatchBuildings: (batchBuildings) => {
        const newBuildings: SavedBuilding[] = batchBuildings.map((building, index) => ({
          ...building,
          // バッチ保存時のID重複防止（インデックスと遅延を使用）
          id: generateBuildingId(index * 50),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))

        set(state => ({
          buildings: enforceMaxLimit(
            [...newBuildings, ...state.buildings],
            state.maxBuildings
          )
        }))
      },

      saveBuilding: async (building: BuildingResult, input?: BuildingInput) => {
        const savedBuilding: SavedBuilding = {
          id: generateBuildingId(),
          title: building.basicInfo.name,
          input: input || {
            theme: building.basicInfo.type,
            buildingType: building.basicInfo.type as any,
            scale: 'house',
            purpose: building.basicInfo.purpose,
            language: building.metadata.language as 'ja' | 'en'
          },
          result: building,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tags: [],
          favorite: false
        }

        set((state) => ({
          buildings: enforceMaxLimit(
            [savedBuilding, ...state.buildings],
            state.maxBuildings
          )
        }))

        // 少し遅延を入れて重複を防ぐ
        await new Promise(resolve => setTimeout(resolve, 50))
      },

      updateBuilding: (id, updates) => {
        set(state => ({
          buildings: state.buildings.map(building =>
            building.id === id 
              ? { ...building, ...updates, updatedAt: new Date().toISOString() }
              : building
          )
        }))
      },

      deleteBuilding: (id) => {
        set(state => ({
          buildings: state.buildings.filter(b => b.id !== id)
        }))
      },

      removeBuilding: (id) => {
        get().deleteBuilding(id)
      },

      // ====================================
      // 取得・検索
      // ====================================

      getBuildingById: (id) => {
        return get().buildings.find(b => b.id === id)
      },

      getBuildingsByWorld: (worldId) => {
        return get().buildings.filter(b => b.worldId === worldId)
      },

      getBuildingsByCharacter: (characterId) => {
        return get().buildings.filter(b => 
          b.characterIds?.includes(characterId)
        )
      },

      getBuildingsByItem: (itemId) => {
        return get().buildings.filter(b => 
          b.itemIds?.includes(itemId)
        )
      },

      searchBuildings: (query) => {
        const lowerQuery = query.toLowerCase()
        return get().buildings.filter(b => 
          b.title.toLowerCase().includes(lowerQuery) ||
          b.result.basicInfo.description?.toLowerCase().includes(lowerQuery) ||
          b.tags?.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
          b.memo?.toLowerCase().includes(lowerQuery)
        )
      },

      // ====================================
      // お気に入り・タグ
      // ====================================

      toggleFavorite: (id) => {
        set(state => ({
          buildings: state.buildings.map(b =>
            b.id === id
              ? { ...b, favorite: !b.favorite, updatedAt: new Date().toISOString() }
              : b
          )
        }))
      },

      addTag: (id, tag) => {
        set(state => ({
          buildings: state.buildings.map(b =>
            b.id === id
              ? { 
                  ...b, 
                  tags: [...(b.tags || []), tag],
                  updatedAt: new Date().toISOString() 
                }
              : b
          )
        }))
      },

      removeTag: (id, tag) => {
        set(state => ({
          buildings: state.buildings.map(b =>
            b.id === id
              ? { 
                  ...b, 
                  tags: b.tags?.filter(t => t !== tag) || [],
                  updatedAt: new Date().toISOString() 
                }
              : b
          )
        }))
      },

      updateMemo: (id, memo) => {
        set(state => ({
          buildings: state.buildings.map(b =>
            b.id === id
              ? { ...b, memo, updatedAt: new Date().toISOString() }
              : b
          )
        }))
      },

      // ====================================
      // 建造物間の関係
      // ====================================

      linkBuildings: (id1, id2, relationship) => {
        set(state => ({
          buildings: state.buildings.map(b => {
            if (b.id === id1) {
              return {
                ...b,
                linkedBuildingIds: [...(b.linkedBuildingIds || []), id2],
                updatedAt: new Date().toISOString()
              }
            }
            if (b.id === id2) {
              return {
                ...b,
                linkedBuildingIds: [...(b.linkedBuildingIds || []), id1],
                updatedAt: new Date().toISOString()
              }
            }
            return b
          })
        }))
      },

      unlinkBuildings: (id1, id2) => {
        set(state => ({
          buildings: state.buildings.map(b => {
            if (b.id === id1) {
              return {
                ...b,
                linkedBuildingIds: b.linkedBuildingIds?.filter(id => id !== id2),
                updatedAt: new Date().toISOString()
              }
            }
            if (b.id === id2) {
              return {
                ...b,
                linkedBuildingIds: b.linkedBuildingIds?.filter(id => id !== id1),
                updatedAt: new Date().toISOString()
              }
            }
            return b
          })
        }))
      },

      getLinkedBuildings: (id) => {
        const building = get().getBuildingById(id)
        if (!building?.linkedBuildingIds) return []
        
        return get().buildings.filter(b => 
          building.linkedBuildingIds?.includes(b.id)
        )
      },

      // ====================================
      // 地区管理
      // ====================================

      createDistrict: (buildingIds, districtName) => {
        const groupId = `district_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        
        set(state => ({
          buildings: state.buildings.map(b =>
            buildingIds.includes(b.id)
              ? { 
                  ...b, 
                  groupId,
                  mapData: {
                    ...b.mapData,
                    district: districtName
                  },
                  updatedAt: new Date().toISOString() 
                }
              : b
          )
        }))
      },

      getDistrictBuildings: (districtName) => {
        return get().buildings.filter(b => 
          b.mapData?.district === districtName
        )
      },

      // ====================================
      // データ整合性
      // ====================================

      fixDuplicateIds: () => {
        const { buildings } = get()
        const idMap = new Map<string, number>()
        const duplicates: string[] = []
        
        // 重複IDを検出
        buildings.forEach(building => {
          const count = idMap.get(building.id) || 0
          idMap.set(building.id, count + 1)
          if (count > 0) {
            duplicates.push(building.id)
          }
        })
        
        if (duplicates.length === 0) {
          return false
        }
        
        console.log(`[Building Store] Found ${duplicates.length} duplicate IDs, fixing...`)
        
        // 重複IDを修正
        set(state => ({
          buildings: state.buildings.map(building => {
            if (duplicates.includes(building.id)) {
              return {
                ...building,
                id: generateBuildingId(),
                updatedAt: new Date().toISOString()
              }
            }
            return building
          })
        }))
        
        return true
      },

      validateBuildingData: (building) => {
        // 必須フィールドのチェック
        if (!building.id || !building.title || !building.result) {
          return false
        }
        
        // 基本情報のチェック
        if (!building.result.basicInfo?.name) {
          return false
        }
        
        return true
      },

      cleanupOrphanedRelations: () => {
        const existingIds = new Set(get().buildings.map(b => b.id))
        
        set(state => ({
          buildings: state.buildings.map(b => ({
            ...b,
            linkedBuildingIds: b.linkedBuildingIds?.filter(id => 
              existingIds.has(id)
            ),
            characterIds: b.characterIds?.filter(id => 
              // キャラクターストアとの連携が必要
              true
            ),
            itemIds: b.itemIds?.filter(id => 
              // アイテムストアとの連携が必要
              true
            )
          }))
        }))
      },

      // ====================================
      // インポート/エクスポート
      // ====================================

      importBuildings: (data) => {
        let importedBuildings: SavedBuilding[]
        
        // BuildingExportData形式とSavedBuilding[]形式の両方に対応
        if ('version' in data && 'buildings' in data) {
          importedBuildings = (data as BuildingExportData).buildings
        } else if (Array.isArray(data)) {
          importedBuildings = data
        } else {
          console.error('Invalid import data format')
          return
        }
        
        // バリデーション
        const validBuildings = importedBuildings.filter(b => 
          get().validateBuildingData(b)
        )
        
        if (validBuildings.length === 0) {
          throw new Error('有効な建造物データが見つかりません')
        }
        
        // ID重複防止のため新しいIDを生成
        const newBuildings: SavedBuilding[] = validBuildings.map((building, index) => ({
          ...building,
          id: generateBuildingId(index * 50),
          createdAt: building.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))
        
        set(state => ({
          buildings: enforceMaxLimit(
            [...newBuildings, ...state.buildings],
            state.maxBuildings
          )
        }))
        
        console.log(`[Building Store] Imported ${newBuildings.length} buildings`)
      },

      exportBuildings: (buildingIds) => {
        const state = get()
        const buildingsToExport = buildingIds 
          ? state.buildings.filter(b => buildingIds.includes(b.id))
          : state.buildings
        
        const exportData: BuildingExportData = {
          version: '1.0.0',
          exportedAt: new Date().toISOString(),
          buildings: buildingsToExport,
          settings: {
            defaultScale: 'medium',
            defaultEra: '中世'
          }
        }

        const dataStr = JSON.stringify(exportData, null, 2)
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
        const exportFileDefaultName = `buildings_export_${Date.now()}.json`
        
        const linkElement = document.createElement('a')
        linkElement.setAttribute('href', dataUri)
        linkElement.setAttribute('download', exportFileDefaultName)
        linkElement.click()
      },

      exportAsJSON: () => {
        const state = get()
        const exportData: BuildingExportData = {
          version: '1.0.0',
          exportedAt: new Date().toISOString(),
          buildings: state.buildings,
          settings: {
            defaultScale: 'medium',
            defaultEra: '中世'
          }
        }
        return JSON.stringify(exportData, null, 2)
      },

      exportAsText: () => {
        const state = get()
        let text = '建造物データ エクスポート\n'
        text += `生成日時: ${new Date().toLocaleString('ja-JP')}\n`
        text += `建造物数: ${state.buildings.length}個\n`
        text += '=' .repeat(50) + '\n\n'
        
        state.buildings.forEach((building, index) => {
          text += `【${index + 1}. ${building.title}】\n`
          text += `ID: ${building.id}\n`
          text += `作成日: ${new Date(building.createdAt).toLocaleString('ja-JP')}\n`
          if (building.favorite) text += '⭐ お気に入り\n'
          
          const result = building.result
          text += `\n■ 基本情報\n`
          text += `名称: ${result.basicInfo.name}\n`
          text += `タイプ: ${result.basicInfo.type}\n`
          text += `規模: ${result.basicInfo.scale}\n`
          text += `用途: ${result.basicInfo.purpose || '不明'}\n`
          text += `建築様式: ${result.exterior?.architecturalStyle || '不明'}\n`
          if (result.basicInfo.description) {
            text += `説明: ${result.basicInfo.description}\n`
          }
          
          if (result.exterior) {
            text += `\n■ 外観\n`
            text += `建材: ${result.exterior.materials?.primary || '不明'}\n`
            if (result.exterior.materials?.secondary?.length) {
              text += `副建材: ${result.exterior.materials.secondary.join('、')}\n`
            }
            text += `色調: ${result.exterior.facade?.colorScheme?.join('、') || '不明'}\n`
            if (result.exterior.visualDescription) text += `外観: ${result.exterior.visualDescription}\n`
          }
          
          if (result.interior) {
            text += `\n■ 内装\n`
            text += `レイアウト: ${result.interior.layout?.floorPlan || '不明'}\n`
            text += `部屋数: ${result.interior.rooms?.length || '不明'}\n`
            if (result.interior.decorStyle?.overall) {
              text += `装飾様式: ${result.interior.decorStyle.overall}\n`
            }
            if (result.interior.rooms?.length) {
              text += `主要な部屋:\n`
              result.interior.rooms.slice(0, 5).forEach(room => {
                text += `  - ${room.name}: ${room.description}\n`
              })
            }
          }
          
          if (result.history) {
            text += `\n■ 歴史\n`
            text += `建築年代: ${result.history.construction?.year || '不明'}\n`
            text += `建築者: ${result.history.construction?.builder || '不明'}\n`
            if (result.history.timeline?.length) {
              text += `歴史的出来事:\n`
              result.history.timeline.forEach(event => {
                text += `  - ${event.era}: ${event.event}\n`
              })
            }
          }
          
          if (building.tags?.length) {
            text += `\nタグ: ${building.tags.join(', ')}\n`
          }
          
          if (building.memo) {
            text += `メモ: ${building.memo}\n`
          }
          
          text += '\n' + '-'.repeat(50) + '\n\n'
        })
        
        // ダウンロード処理
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `buildings_export_${Date.now()}.txt`
        link.click()
        URL.revokeObjectURL(url)
      },

      // ====================================
      // 一括操作
      // ====================================

      clearAllBuildings: () => {
        if (confirm('すべての建造物を削除してもよろしいですか？この操作は取り消せません。')) {
          set({ buildings: [] })
        }
      },

      clearAll: () => {
        get().clearAllBuildings()
      },

      deleteMultiple: (ids) => {
        set(state => ({
          buildings: state.buildings.filter(b => !ids.includes(b.id))
        }))
      },

      batchUpdateWorld: (buildingIds, worldId) => {
        set(state => ({
          buildings: state.buildings.map(b =>
            buildingIds.includes(b.id)
              ? { ...b, worldId, updatedAt: new Date().toISOString() }
              : b
          )
        }))
      },

      // ====================================
      // 統計情報
      // ====================================

      getStatistics: () => {
        const buildings = get().buildings
        
        const byScale: Record<string, number> = {}
        const byType: Record<string, number> = {}
        const byWorld: Record<string, number> = {}
        
        buildings.forEach(building => {
          // 規模別集計
          const scale = building.result.basicInfo.scale
          byScale[scale] = (byScale[scale] || 0) + 1
          
          // タイプ別集計
          const type = building.result.basicInfo.type
          byType[type] = (byType[type] || 0) + 1
          
          // 世界観別集計
          if (building.worldId) {
            byWorld[building.worldId] = (byWorld[building.worldId] || 0) + 1
          }
        })
        
        return {
          total: buildings.length,
          byScale,
          byType,
          byWorld,
          favorites: buildings.filter(b => b.favorite).length
        }
      }
    }),
    {
      name: 'building-store',
      storage: createIndexedDBStorage('building-store'),
      version: 1,
      partialize: (state) => ({
        buildings: state.buildings
      })
    }
  )
)

// ====================================
// ヘルパー関数
// ====================================

// ID生成関数（重複防止のため遅延を考慮）
function generateBuildingId(delay: number = 0): string {
  const timestamp = Date.now() + delay
  const random = Math.random().toString(36).substring(2, 11)
  return `building_${timestamp}_${random}`
}

// 最大数制限を適用
function enforceMaxLimit(buildings: SavedBuilding[], maxBuildings: number): SavedBuilding[] {
  if (buildings.length <= maxBuildings) {
    return buildings
  }
  
  // お気に入り以外の古いものから削除
  const favorites = buildings.filter(b => b.favorite)
  const nonFavorites = buildings.filter(b => !b.favorite)
  
  if (nonFavorites.length > 0) {
    const toKeep = [...favorites, ...nonFavorites.slice(0, maxBuildings - favorites.length)]
    return toKeep.slice(0, maxBuildings)
  }
  
  // すべてお気に入りの場合は単純に制限
  return buildings.slice(0, maxBuildings)
}